import type { JsonObject, RenderNode, RenderTree } from '@inf-monkeys-tech/monkeys';
import { compileRenderTree } from '@inf-monkeys-tech/monkeys/runtime';
import { useMemo } from 'react';
import type { ReactNode, Ref } from 'react';

import { RenderNodeHost } from './RenderNodeHost';
import type { RenderNodeHostElement, RenderNodeHostProps, RenderNodeRuntimeState } from './RenderNodeHost';

export interface RenderTreeNodeContext extends RenderNodeRuntimeState {
  node: RenderNode;
  scope?: JsonObject;
  instanceKey?: string;
  children: ReactNode;
  childNodes: readonly RenderNode[];
  childrenBySlot: Readonly<Record<string, ReactNode>>;
}

export interface RenderTreeHostProps {
  tree: RenderTree;
  maxMaterializedNodes?: number;
  resolveNodeInstances?: (node: RenderNode, parentScope?: JsonObject) => readonly { key: string; scope: JsonObject }[] | undefined;
  wrapNode?: (context: { node: RenderNode; scope?: JsonObject; instanceKey?: string; content: ReactNode }) => ReactNode;
  activeNodeIds?: ReadonlySet<string>;
  visibleNodeIds?: ReadonlySet<string>;
  manualQueryNodeIds?: ReadonlySet<string>;
  loading?: ReactNode;
  empty?: ReactNode;
  error?: ReactNode;
  unframedForNode?: (node: RenderNode) => boolean;
  asChildForNode?: (node: RenderNode) => boolean;
  elementForNode?: (node: RenderNode) => RenderNodeHostElement;
  classNameForNode?: (node: RenderNode) => string | undefined;
  elementRefForNode?: (node: RenderNode) => Ref<HTMLElement> | undefined;
  scrollRestoration?: RenderNodeHostProps['scrollRestoration'];
  renderNode?: (context: RenderTreeNodeContext) => ReactNode;
  onActivate?: RenderNodeHostProps['onActivate'];
  onManualQueryEnabledChange?: RenderNodeHostProps['onManualQueryEnabledChange'];
  onRuntimeStateChange?: RenderNodeHostProps['onRuntimeStateChange'];
}

const defaultElementForNode = (node: RenderNode): RenderNodeHostElement => {
  if (node.kind === 'shell') return 'main';
  if (node.kind === 'navigation') return 'nav';
  if (node.kind === 'detail') return 'aside';
  if (node.kind === 'record') return 'article';
  return 'section';
};

export function RenderTreeHost({
  tree,
  maxMaterializedNodes = 4096,
  resolveNodeInstances,
  wrapNode,
  activeNodeIds,
  visibleNodeIds,
  manualQueryNodeIds,
  loading,
  empty,
  error,
  elementForNode = defaultElementForNode,
  asChildForNode,
  unframedForNode,
  classNameForNode,
  elementRefForNode,
  scrollRestoration,
  renderNode,
  onActivate,
  onManualQueryEnabledChange,
  onRuntimeStateChange,
}: RenderTreeHostProps) {
  const compiled = useMemo(() => compileRenderTree(tree), [tree]);
  if (!Number.isSafeInteger(maxMaterializedNodes) || maxMaterializedNodes < 1 || maxMaterializedNodes > 4096) throw new RangeError('RenderTree materialization budget must be an integer from 1 to 4096.');
  let materializedNodes = 0;

  const renderBranch = (node: RenderNode, parentScope?: JsonObject, parentInstanceKey?: string): ReactNode => {
    const instances = resolveNodeInstances?.(node, parentScope);
    if (instances === undefined) return renderInstance(node, parentScope, parentInstanceKey);
    const keys = new Set<string>();
    const rendered = instances.map(({ key, scope }) => {
      if (!key || keys.has(key)) throw new TypeError(`RenderNode ${node.nodeId} requires unique non-empty instance keys.`);
      keys.add(key);
      return renderInstance(node, scope, JSON.stringify([parentInstanceKey ?? null, node.nodeId, key]));
    });
    return rendered.length === 1 ? rendered[0] : rendered;
  };

  const renderInstance = (node: RenderNode, scope?: JsonObject, instanceKey?: string): ReactNode => {
    if (++materializedNodes > maxMaterializedNodes) throw new RangeError('RenderTree materialization budget exceeded.');
    const runtimeNode = instanceKey && node.scroll.restoreKey
      ? { ...node, scroll: { ...node.scroll, restoreKey: JSON.stringify([node.scroll.restoreKey, instanceKey]) } }
      : node;
    const childNodes = compiled.childrenOf(node.nodeId);
    const descendants = childNodes.map((child) => renderBranch(child, scope, instanceKey));
    const descendantsBySlot = childNodes.reduce<Record<string, ReactNode[]>>((slots, child, index) => {
      const slot = child.slot ?? child.nodeId;
      (slots[slot] ??= []).push(descendants[index]);
      return slots;
    }, {});
    const childrenBySlot = Object.freeze(Object.fromEntries(
      Object.entries(descendantsBySlot).map(([slot, slotDescendants]) => [
        slot,
        slotDescendants.length === 1 ? slotDescendants[0] : <>{slotDescendants}</>,
      ]),
    ));
    return (
      <RenderNodeHost
        key={JSON.stringify([node.nodeId, instanceKey ?? null])}
        node={runtimeNode}
        data-monkeys-instance-key={instanceKey}
        as={elementForNode(node)}
        asChild={asChildForNode?.(node)}
        unframed={unframedForNode?.(node)}
        active={activeNodeIds?.has(node.nodeId) ?? true}
        visible={visibleNodeIds?.has(node.nodeId) ?? true}
        manualQueryEnabled={manualQueryNodeIds?.has(node.nodeId) ?? false}
        loading={loading}
        empty={empty}
        error={error}
        className={classNameForNode?.(node)}
        elementRef={elementRefForNode?.(node)}
        scrollRestoration={scrollRestoration}
        onActivate={onActivate}
        onManualQueryEnabledChange={onManualQueryEnabledChange}
        onRuntimeStateChange={onRuntimeStateChange}
      >
        {(runtime) => {
          const content = renderNode?.({
            ...runtime,
            node: runtimeNode,
            scope,
            instanceKey,
            children: descendants,
            childNodes,
            childrenBySlot,
          }) ?? descendants;
          return wrapNode ? wrapNode({ node: runtimeNode, scope, instanceKey, content }) : content;
        }}
      </RenderNodeHost>
    );
  };

  return <>{renderBranch(compiled.root)}</>;
}
